/**
 * 🆕 Feb 2026 — guaranteeMatch.js
 * Normalizza il nome di una garanzia estratta da PDF e la confronta con il
 * listino della compagnia (masterGuarantees). Restituisce il match trovato
 * (o null) applicando in ordine:
 *   (a) alias configurati sulla voce di listino (campo `aliases`)
 *   (b) alias di default globali (mappa DEFAULT_ALIASES qui sotto)
 *   (c) nome normalizzato esatto
 *   (d) sottostringa (l'una contiene l'altra)
 *   (e) sovrapposizione di token (>=1 parola in comune >=3 caratteri)
 *
 * La normalizzazione elimina gli accenti (NFD), porta in minuscolo, rimuove
 * la punteggiatura e i suffissi tra parentesi (es. "Responsabilità civile (RCA)"
 * → "responsabilita civile"). Gli alias di default sono espressi in forma
 * normalizzata e mappano più diciture al nome canonico della voce di listino.
 *
 * Non modifica nessuna formula di calcolo di premio/netto/imposte/SSN.
 */

// Rimuove suffissi/parti tra parentesi (es. "Nome (esteso)" → "Nome"),
// elimina accenti, porta in lowercase, sostituisce la punteggiatura con
// spazi singoli e trimma.
export function normalizeGuaranteeName(input) {
  if (input == null) return "";
  return String(input)
    .replace(/\([^)]*\)/g, " ")            // rimuovi contenuto fra parentesi tonde
    .replace(/\[[^\]]*\]/g, " ")           // e fra parentesi quadre
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // togli accenti
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Mappa alias globali: dicitura normalizzata → nome canonico normalizzato.
// Usati come "primo scarto" quando nessuna voce di listino ha alias espliciti.
// La chiave e il valore vengono normalizzati con normalizeGuaranteeName.
const DEFAULT_ALIASES_RAW = {
  "Responsabilità civile": "Rca auto",
  "Responsabilità civile (RCA)": "Rca auto",
  "Responsabilità Civile Auto": "Rca auto",
  "RCA - Responsabilità Civile Auto": "Rca auto",
  "RCA": "Rca auto",
  "RC auto": "Rca auto",
  "Furto e incendio": "Incendio/Furto",
  "Assistenza stradale Base": "Assistenza stradale base",
  "Infortuni del Conducente": "Infortuni del conducente",
};

// Pre-normalizzata (una tantum)
const DEFAULT_ALIASES = Object.fromEntries(
  Object.entries(DEFAULT_ALIASES_RAW).map(([k, v]) => [
    normalizeGuaranteeName(k),
    normalizeGuaranteeName(v),
  ])
);

/**
 * Trova la voce di listino che meglio corrisponde al nome estratto.
 * @param {string} extractedName - nome grezzo dalla estrazione PDF/AI
 * @param {Array<{id:string,name:string,aliases?:string[],imposte_percent?:number,ssn_percent?:number}>} masterGuarantees
 * @returns {object|null} la voce di listino agganciata (null = non trovata)
 */
export function resolveGuarantee(extractedName, masterGuarantees) {
  const target = normalizeGuaranteeName(extractedName);
  if (!target || !Array.isArray(masterGuarantees) || masterGuarantees.length === 0) return null;

  // (a) alias per-voce (campo `aliases` sulla master)
  for (const g of masterGuarantees) {
    const gAliases = Array.isArray(g.aliases) ? g.aliases : [];
    for (const a of gAliases) {
      if (normalizeGuaranteeName(a) === target) return g;
    }
  }

  // (b) alias globali → risolvi a nome canonico normalizzato
  const canonicalNormalized = DEFAULT_ALIASES[target];
  if (canonicalNormalized) {
    const hit = masterGuarantees.find((g) => normalizeGuaranteeName(g.name) === canonicalNormalized);
    if (hit) return hit;
  }

  // (c) match esatto per nome normalizzato
  let m = masterGuarantees.find((g) => normalizeGuaranteeName(g.name) === target);
  if (m) return m;

  // (d) sottostringa (l'una contiene l'altra)
  m = masterGuarantees.find((g) => {
    const n = normalizeGuaranteeName(g.name);
    return n && (n.includes(target) || target.includes(n));
  });
  if (m) return m;

  // (e) token overlap ≥1 parola in comune ≥3 caratteri
  const tExt = new Set(target.split(" ").filter((t) => t.length >= 3));
  if (tExt.size === 0) return null;
  m = masterGuarantees.find((g) => {
    const tMas = new Set(normalizeGuaranteeName(g.name).split(" ").filter((t) => t.length >= 3));
    for (const t of tExt) if (tMas.has(t)) return true;
    return false;
  });
  return m || null;
}

/**
 * Genera un `code` slug stabile: minuscolo, senza accenti, snake_case, senza
 * troncamenti brutali (che perdevano semantica). Non contiene punteggiatura
 * né spazi. Non modifica il nome visibile.
 */
export function buildGuaranteeCode(nameOrCode) {
  if (nameOrCode == null) return "garanzia";
  const s = String(nameOrCode)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return s || "garanzia";
}
